import { coverUrl } from '../utils/coverUrl';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OfflineSaveButton from './OfflineSaveButton';
import { supabase } from '../supabaseClient';
import { showReceipt } from './PurchaseReceipt';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { downloadTrack } from '../utils/downloadTrack';
import PriceBreakdown, { useQuote } from './PriceBreakdown';
import {
    X, Share2, ListMusic, Download, Heart, Play, Music, Loader, Check,
    ChevronLeft, ShoppingCart, Lock, PlusCircle, DollarSign, Clock, Info,
    MessageCircle,
} from 'lucide-react';
import ShareCard from './ShareCard';
import { buildShareUrl } from '../utils/shareLink';
import { TrackCommentsOverlay } from './TrackComments';
import PayOptions from './PayOptions';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

function formatReleaseDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TrackActionSheet({ track, artist, onClose, initialView = 'main' }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { addToQueue, playTrack } = usePlayer();
    // initialView lets a caller open straight on the price. The sheet already
    // switches here itself when a download comes back purchase_required; this
    // is for the paths that discover the same thing somewhere else, such as an
    // offline save or the download button on the track page.
    const [view, setView] = useState(initialView);
    const [showComments, setShowComments] = useState(false);
    const [commentCount, setCommentCount] = useState(0);
    const [playlists, setPlaylists] = useState([]);
    const [addingTo, setAddingTo] = useState(null);
    const [addedTo, setAddedTo] = useState({});
    const [shared, setShared] = useState(false);
    const [showShareCard, setShowShareCard] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [liked, setLiked] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [creatingPlaylist, setCreatingPlaylist] = useState(false);
    const [showNewPlaylist, setShowNewPlaylist] = useState(false);

    // Purchase state
    const [paypalReady, setPaypalReady] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);
    const [purchaseError, setPurchaseError] = useState('');

    // Pay What You Want state
    const isPWYW = track?.pay_what_you_want === true;
    const minimumPrice = track?.minimum_price != null ? parseFloat(track.minimum_price) : 0;

    const [fanPrice, setFanPrice] = useState('');
    const [fanPriceError, setFanPriceError] = useState('');

    // WHAT THIS TRACK COSTS, AND WHY THE SERVER DECIDES IT
    //
    // This used to be worked out here:
    //
    //   track.download_price > 0 ? track.download_price
    //                            : (track.albums?.price || track.album_price || 0)
    //
    // Two things wrong with it, and together they produced "Purchase to
    // download  $0.00" with a Download Free button under it on a track that
    // is for sale, which then 403s from get-download-url because the server
    // knows it is not free.
    //
    //   1. A track on a priced album usually has no download_price of its own.
    //      Its price is the ALBUM price divided by the number of published
    //      tracks, which is what paypal-order and get-download-url both work
    //      out. The line above used the whole album price when it had it,
    //      which is a different (and too high) number.
    //   2. The action sheet is opened from the player and from rows that
    //      select tracks without the albums embed, so `track.albums` is
    //      usually undefined and the fallback landed on 0.
    //
    // So the price comes from the server, through the same quote endpoint
    // every other purchase screen uses, and resolved by the same code the
    // checkout uses. The track's own download_price is the only local value
    // still trusted, and only as something to show while the quote is in
    // flight. See the note at the top of src/components/PriceBreakdown.js.
    const localPrice = Number(track?.download_price) > 0 ? Number(track.download_price) : 0;

    const { quote, loading: quoteLoading, refresh: refreshQuote } = useQuote(
        track?.id && view === 'purchase' && !isPWYW
            ? { trackId: track.id }
            : (track?.id && view === 'purchase' && isPWYW && parseFloat(fanPrice) > 0
                ? { trackId: track.id, amount: parseFloat(fanPrice) }
                : null),
        isPWYW ? 400 : 0
    );

    const quotedPrice = quote?.artistPrice != null ? Number(quote.artistPrice) : null;
    const basePrice   = quotedPrice != null ? quotedPrice : localPrice;

    const effectivePrice = isPWYW ? (parseFloat(fanPrice) || 0) : basePrice;

    // True while we genuinely do not know yet. Without this the sheet shows a
    // Download Free button for the moment before the quote lands, which is the
    // button that 403s.
    const priceUnknown = !isPWYW && quotedPrice == null && (quoteLoading || localPrice <= 0);

    // IS THERE A PRICE TO CHARGE YET?
    //
    // On a track that carries its own download_price this is true on the first
    // render, from the track row itself. On a track priced by its ALBUM it is
    // false until the quote comes back, because the track's own price is zero
    // and the album share is worked out on the server.
    //
    // The PayPal script loader below used to read effectivePrice directly and
    // depend only on [view, track.id]. On an album-priced track that meant it
    // ran once, at zero, returned without loading anything, and never ran
    // again when the real price arrived. paypalReady stayed false for the life
    // of the sheet: a spinner that never stops and an empty white box under
    // it, on every track in an album, with nothing in the console.
    //
    // A boolean rather than the number on purpose. It flips once, so the
    // script is still fetched once, and a pay-what-you-want buyer typing an
    // amount does not re-request the SDK on every keystroke.
    const hasChargeablePrice = isPWYW
        ? (parseFloat(fanPrice) || 0) > 0
        : effectivePrice > 0;

    const isPreorder = track?.is_preorder === true;
    const releaseDate = track?.release_date || null;
    const isNotYetReleased = isPreorder && releaseDate && new Date(releaseDate) > new Date();
    const [alreadyPreordered, setAlreadyPreordered] = useState(false);

    useEffect(() => {
        if (!track) return;
        if (user) {
            checkLike();
            if (isPreorder) checkAlreadyPreordered();
        }
        if (isPWYW) {
            const suggested = Math.max(basePrice, minimumPrice);
            setFanPrice(suggested > 0 ? suggested.toFixed(2) : '');
        }
        loadCommentCount();
    }, [track?.id]);

    // The number beside Comments. Read again when the sheet closes so leaving
    // a comment updates it without a round trip on every render.
    const loadCommentCount = async () => {
        if (!track?.id) return;
        const { count, error } = await supabase
            .from('track_comments')
            .select('id', { count: 'exact', head: true })
            .eq('track_id', track.id);
        if (error) { console.error('[TrackActionSheet] comment count failed:', error.code, error.message); return; }
        setCommentCount(count || 0);
    };

    useEffect(() => {
        if (view !== 'purchase' || !track) return;
        // Don't load PayPal for free/PWYW-zero tracks, or before the price of
        // an album-priced track is known. See hasChargeablePrice above.
        if (!hasChargeablePrice) return;
        setPaypalReady(false);
        setPurchaseError('');
        const existing = document.getElementById('paypal-sdk-tas');
        if (existing) existing.remove();
        const script = document.createElement('script');
        script.id = 'paypal-sdk-tas';
        script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD`;
        script.async = true;
        script.onload = () => setPaypalReady(true);
        script.onerror = () => setPurchaseError('Failed to load PayPal. Please try again.');
        document.head.appendChild(script);
    }, [view, track?.id, hasChargeablePrice]);

    useEffect(() => {
        if (!paypalReady || view !== 'purchase' || !window.paypal || !track) return;
        if (isPWYW && !validateFanPrice()) return;
        if (effectivePrice <= 0) return;
        // LOOK AGAIN INSTEAD OF GIVING UP.
        //
        // This block used to read: find the container, and if it is not there,
        // return. Everything below is unchanged from that version, including
        // the trigger, the dependencies and the render call. The only
        // difference is what happens when the container is not ready.
        //
        // Two ways it is not ready. It is not in the DOM yet, because it is
        // only rendered once the price is known and the quote has not landed.
        // Or it is in the DOM with a width of zero, because the sheet is still
        // sliding up, and the SDK will not render into something it cannot
        // measure. In both cases the old code returned and never came back,
        // and the buyer was left with the empty white box.
        //
        // So it looks again on the next frame, for up to ten seconds, and
        // nothing is cleared or rendered until there is a container with a
        // real width. A screen where the buttons already drew is untouched:
        // on that path the first look succeeds, exactly as it always did.
        let cancelled = false;
        let frame = null;
        const deadline = Date.now() + 10000;

        const draw = () => {
            if (cancelled) return;

            const container = document.getElementById('paypal-tas-container');
            if (!container || container.offsetWidth < 1) {
                if (Date.now() > deadline) {
                    console.error('[TrackActionSheet] no measurable PayPal container after 10s',
                        { found: !!container, width: container ? container.offsetWidth : null });
                    setPurchaseError('PayPal could not load here. Try the other payment option, or reload the page.');
                    return;
                }
                frame = requestAnimationFrame(draw);
                return;
            }

            container.innerHTML = '';

            window.paypal.Buttons({
            style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' },
            createOrder: async () => {
                setPurchasing(true);
                setPurchaseError('');
                try {
                    const res = await fetch('/.netlify/functions/paypal-order', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'create',
                            trackId: track.id,
                            amount: effectivePrice,
                            trackTitle: track.title,
                            artistName: artist?.artist_name,
                        }),
                    });
                    const { orderId, error } = await res.json();
                    if (error || !orderId) throw new Error(error || 'Failed to create order');
                    return orderId;
                } catch (err) {
                    setPurchaseError(err.message);
                    setPurchasing(false);
                    throw err;
                }
            },
            onApprove: async (data) => {
                try {
                    const res = await fetch('/.netlify/functions/paypal-order', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'capture', orderId: data.orderID, userId: user?.id }),
                    });
                    const captureData = await res.json();
                    if (!captureData.success) throw new Error('Payment capture failed');

                    // `success` only ever meant "PayPal took the money". It did
                    // NOT mean the sale was recorded or the download granted -
                    // and when the recording step silently did nothing, this
                    // showed a green tick to somebody who had just paid for a
                    // file they were then refused. The function now reports
                    // whether the grant was written, and this believes it.
                    if (captureData.recorded === false) {
                      setPurchasing(false);
                      setPurchaseError(
                        'Your payment went through, but we could not unlock the download automatically. '
                        + 'Nothing further is owed, contact us with this reference and we will send it straight over: '
                        + (captureData.captureId || 'no reference')
                      );
                      console.error('[purchase] paid but not recorded:',
                        captureData.captureId, captureData.recordError);
                      return;
                    }

                    setPurchaseSuccess(true);
                    setPurchasing(false);
                    showReceipt({
                      kind: 'purchase',
                      title: track.title,
                      subtitle: artist?.artist_name,
                      amount: captureData.amount ?? (effectivePrice),
                      ...(isPreorder && isNotYetReleased
                        ? { heading: 'Pre-order placed',
                            note: 'You will get the download the moment it is released. '
                                + 'There is a copy of this in your notifications.' }
                        : {}),
                    });

                    if (isPreorder && isNotYetReleased) {
                        setAlreadyPreordered(true);
                        setTimeout(() => { onClose(); }, 2500);
                        return;
                    }

                    setTimeout(async () => {
                        try {
                            const { data: { session } } = await supabase.auth.getSession();
                            await downloadTrack(track.id, track.title, session?.access_token);
                            onClose();
                        } catch (dlErr) {
                            // `catch {}`, the download failing right after a
                            // successful payment was the single most important
                            // error in this file to surface, and it was the one
                            // being thrown away. The sheet now stays open and
                            // says so, instead of closing on a paid-for file
                            // that never arrived.
                            console.error('[purchase] post-payment download failed:', dlErr.message);
                            setPurchaseSuccess(false);
                            setPurchaseError(
                              'Payment received, but the download did not start. '
                              + 'It is saved to your account, open Library \u2192 Downloads to get it.'
                            );
                        }
                    }, 1500);
                } catch (err) {
                    setPurchaseError(err.message);
                    setPurchasing(false);
                }
            },
            onError: () => {
                setPurchaseError('Payment failed. Please try again.');
                setPurchasing(false);
            },
            onCancel: () => setPurchasing(false),
            }).render('#paypal-tas-container').catch((err) => {
                if (cancelled) return;
                // This promise was never read. Whatever went wrong - the
                // script blocked by a content blocker, the SDK refusing the
                // container, PayPal itself down - the buyer got a white
                // rectangle and no reason for it, and so did the log.
                console.error('[TrackActionSheet] PayPal buttons did not render:', err);
                setPurchaseError('PayPal could not load here. Try the other payment option, or reload the page.');
            });
        };

        draw();
        return () => {
            cancelled = true;
            if (frame) cancelAnimationFrame(frame);
        };
    }, [paypalReady, view, track?.id, effectivePrice]);

    const checkAlreadyPreordered = async () => {
        const { data } = await supabase
            .from('downloads')
            .select('id')
            .eq('track_id', track.id)
            .eq('user_id', user.id)
            .maybeSingle();
        setAlreadyPreordered(!!data);
    };

    const validateFanPrice = () => {
        const val = parseFloat(fanPrice);
        if (isNaN(val) || val < 0) { setFanPriceError('Please enter a valid amount'); return false; }
        if (minimumPrice > 0 && val < minimumPrice) { setFanPriceError(`Minimum is $${minimumPrice.toFixed(2)}`); return false; }
        setFanPriceError('');
        return true;
    };

    const checkLike = async () => {
        const { data } = await supabase
            .from('track_likes').select('id')
            .eq('track_id', track.id).eq('user_id', user.id).maybeSingle();
        setLiked(!!data);
    };

    const loadPlaylists = async () => {
        if (!user) { navigate('/login'); onClose(); return; }
        const { data } = await supabase
            .from('playlists')
            .select('id, name, cover_url, playlist_tracks(id, tracks(cover_artwork_url))')
            .eq('user_id', user.id)
            .order('name');
        setPlaylists(data || []);
        setView('playlists');
    };

    const handleCreatePlaylist = async () => {
        const name = newPlaylistName.trim();
        if (!name || creatingPlaylist) return;
        setCreatingPlaylist(true);
        const { data, error } = await supabase
            .from('playlists')
            .insert({ name, user_id: user.id })
            .select('id, name')
            .single();
        if (!error && data) {
            await handleAddToPlaylist(data.id);
            setPlaylists(prev => [...prev, data]);
        }
        setNewPlaylistName('');
        setShowNewPlaylist(false);
        setCreatingPlaylist(false);
    };

    const handleAddToPlaylist = async (playlistId) => {
        setAddingTo(playlistId);
        const { data: existing } = await supabase
            .from('playlist_tracks').select('id')
            .eq('playlist_id', playlistId).eq('track_id', track.id).maybeSingle();
        if (!existing) {
            const { data: last } = await supabase
                .from('playlist_tracks').select('position')
                .eq('playlist_id', playlistId).order('position', { ascending: false })
                .limit(1).maybeSingle();
            await supabase.from('playlist_tracks').insert({
                playlist_id: playlistId, track_id: track.id,
                position: (last?.position ?? -1) + 1,
            });
        }
        setAddedTo(prev => ({ ...prev, [playlistId]: true }));
        setAddingTo(null);
    };

    const handleShare = () => {
        setShowShareCard(true);
    };

    const handleQueue = () => {
        addToQueue({ ...track, artist_name: artist?.artist_name || track.artist_name });
        onClose();
    };

    const handleLike = async () => {
        if (!user) { navigate('/login'); onClose(); return; }
        if (liked) {
            await supabase.from('track_likes').delete().eq('track_id', track.id).eq('user_id', user.id);
            setLiked(false);
        } else {
            await supabase.from('track_likes').insert({ track_id: track.id, user_id: user.id });
            setLiked(true);
        }
    };

    const handleDownload = async () => {
        if (!user) { navigate('/login'); onClose(); return; }
        setDownloadError(null);
        setDownloading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const authToken = session?.access_token;
            if (!authToken) throw new Error('Not authenticated');
            await downloadTrack(track.id, track.title, authToken);
            onClose();
        } catch (err) {
            if (err.message === 'purchase_required') {
                // This used to insert a downloads row from the browser with
                // amount_paid 0 and retry, "in case the backend has no record
                // for a free track". It never helped: the free branch of
                // get-download-url writes its own grant with the service role,
                // and the paid branch compares amount_paid against the price,
                // so a forged 0 row is refused anyway. What it did do was
                // write a row into the table the server trusts, from the one
                // place that cannot be trusted. Migration 175 takes the
                // insert policy away; this goes with it.
                //
                // purchase_required now means what it says: show the price.
                setDownloading(false);
                setView('purchase');
                return;
            }
            if (err.message === 'insufficient_payment') {
                // They have a download record for this track, but for less
                // than its price (a free download before it was priced, or a
                // price that went up). That is "you need to buy it", not a
                // failure: show the purchase view, same as purchase_required.
                setDownloading(false);
                setView('purchase');
                return;
            }
            if (err.message === 'artists_cannot_download') {
                // Your own track: there is nothing to buy, and the server
                // refuses self-downloads so they cannot inflate the count.
                // Say that, rather than "Download failed. Please try again."
                setDownloading(false);
                setDownloadError('This is your own track, so it can\'t be bought or downloaded from your account.');
                return;
            }
            if (err.message === 'not_released_yet') {
                setDownloading(false);
                setDownloadError(`This track releases on ${formatReleaseDate(releaseDate)}. Check back then!`);
                return;
            }
            setDownloadError('Download failed. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    if (!track) return null;

    const downloadButtonLabel = () => {
        if (downloading) return 'Downloading...';
        if (isPreorder && isNotYetReleased) {
            if (alreadyPreordered) return `Pre-ordered · Releases ${formatReleaseDate(releaseDate)}`;
            return `Pre-order · ${isPWYW ? 'Pay What You Want' : basePrice > 0 ? `$${basePrice.toFixed(2)}` : 'Free'}`;
        }
        if (isPWYW) return 'Buy & Download · Pay What You Want';
        if (basePrice > 0) return `Buy & Download · $${basePrice.toFixed(2)}`;
        return 'Download';
    };

    return (
        <>
        {/* Centred, with room around it, instead of pinned to the bottom edge.
         *
         * It was `items-end` with `max-h-[92dvh]` and no padding, so the sheet
         * ran from wherever its content started all the way to the very bottom
         * of the screen. On a phone that put the last row of a playlist list
         * underneath the home bar, half drawn, with nothing to tell you there
         * was more. Adding to a playlist is the view where that hurt most,
         * because the list is exactly the part you came to read.
         *
         * Centred with 16px of air on every side and a cap of 82% of the
         * screen, the sheet always has a visible top AND a visible bottom, so
         * you can see where it ends and whether there is more to scroll to.
         *
         * The corners are rounded on all four sides now. A sheet with square
         * corners at the bottom is saying it continues off the screen, and this
         * one does not. */}
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div
                className="relative w-full max-w-lg rounded-2xl overflow-x-hidden overflow-y-auto overscroll-contain animate-slide-up"
                style={{
                  backgroundColor: '#111',
                  border: '1px solid rgba(255,255,255,0.08)',
                  maxHeight: 'min(82dvh, calc(100dvh - 32px))',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Track info header */}
                <div className="flex items-center space-x-3 p-4 border-b border-white/[0.06]">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/[0.06] flex-shrink-0">
                        {track.cover_artwork_url
                            ? <img src={coverUrl(track.cover_artwork_url, 400)} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><Music className="w-5 h-5 text-white/20" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white truncate">{track.title}</p>
                            {isPreorder && isNotYetReleased && (
                                <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 uppercase tracking-wide">
                                    Pre-order
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => { const slug = artist?.slug || track.artist_slug; if (slug) { navigate(`/artist/${slug}`); onClose(); } }}
                            className="text-xs text-white/40 truncate hover:text-white/70 transition text-left w-full block"
                        >
                            {artist?.artist_name || track.artist_name}
                        </button>
                    </div>
                    <button
                        onClick={view !== 'main' ? () => { setView('main'); setPurchaseError(''); } : onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/[0.06]"
                    >
                        {view !== 'main' ? <ChevronLeft className="w-4 h-4 text-white/50" /> : <X className="w-4 h-4 text-white/50" />}
                    </button>
                </div>

                {/* Views */}
                <div className="py-2">

                    {/* Playlists view */}
                    {view === 'playlists' && (
                        <div>
                            {showNewPlaylist ? (
                                <div className="flex items-center space-x-2 px-4 py-2.5 border-b border-white/[0.05]">
                                    <input
                                        autoFocus type="text" value={newPlaylistName}
                                        onChange={e => setNewPlaylistName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleCreatePlaylist(); if (e.key === 'Escape') { setShowNewPlaylist(false); setNewPlaylistName(''); } }}
                                        placeholder="Playlist name"
                                        className="flex-1 bg-white/[0.06] text-white text-sm rounded-lg px-3 py-2 outline-none placeholder:text-white/25 border border-white/[0.08] focus:border-white/20"
                                        maxLength={60}
                                    />
                                    <button onClick={handleCreatePlaylist} disabled={!newPlaylistName.trim() || creatingPlaylist}
                                        className="px-3 py-2 rounded-lg text-xs font-semibold text-black bg-white disabled:opacity-40 transition active:scale-95 flex-shrink-0">
                                        {creatingPlaylist ? <Loader className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
                                    </button>
                                    <button onClick={() => { setShowNewPlaylist(false); setNewPlaylistName(''); }} className="p-2 text-white/30 hover:text-white/60 transition flex-shrink-0">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <button onClick={() => setShowNewPlaylist(true)} className="w-full flex items-center space-x-3 px-5 py-3 text-left hover:bg-white/[0.04] transition border-b border-white/[0.05]">
                                    <PlusCircle className="w-4 h-4 text-white/40" />
                                    <span className="text-sm text-white/60">New playlist</span>
                                </button>
                            )}
                            {playlists.length === 0 && !showNewPlaylist && (
                                <p className="text-xs text-white/30 px-5 py-3">No playlists yet, create one above</p>
                            )}
                            {playlists.map(pl => {
                                const covers = (pl.playlist_tracks || [])
                                    .map(pt => pt.tracks?.cover_artwork_url).filter(Boolean);
                                const thumb = pl.cover_url || covers[0] || null;
                                const count = pl.playlist_tracks?.length || 0;
                                return (
                                    <button key={pl.id} onClick={() => handleAddToPlaylist(pl.id)} disabled={addingTo === pl.id}
                                        className="w-full flex items-center space-x-3 px-4 py-2.5 active:bg-white/[0.04] transition">
                                        <div className="w-9 h-9 rounded-lg flex-shrink-0 overflow-hidden"
                                            style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(59,130,246,0.2))' }}>
                                            {thumb
                                                ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                                                : <div className="w-full h-full flex items-center justify-center">
                                                    <Music className="w-4 h-4 text-white/20" />
                                                  </div>}
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                            <p className="text-sm text-white/80 truncate">{pl.name}</p>
                                            <p className="text-xs text-white/30">{count} track{count !== 1 ? 's' : ''}</p>
                                        </div>
                                        {addedTo[pl.id]
                                            ? <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                                            : addingTo === pl.id
                                                ? <Loader className="w-4 h-4 animate-spin text-white/30 flex-shrink-0" />
                                                : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Purchase view */}
                    {view === 'purchase' && (
                        <div className="px-5 py-4 space-y-4">
                            {purchaseSuccess ? (
                                <div className="text-center py-4">
                                    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                                        {isPreorder && isNotYetReleased
                                            ? <Clock className="w-7 h-7 text-yellow-300" />
                                            : <Check className="w-7 h-7 text-white" />}
                                    </div>
                                    {isPreorder && isNotYetReleased ? (
                                        <>
                                            <p className="font-semibold text-white">Pre-order Confirmed!</p>
                                            <p className="text-sm mt-1 text-white/40">
                                                You'll be able to download this track on {formatReleaseDate(releaseDate)}.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="font-semibold text-white">Purchase Complete!</p>
                                            <p className="text-sm mt-1 text-white/40">Starting download...</p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {isPreorder && isNotYetReleased && (
                                        <div className="flex items-center gap-2 rounded-xl p-3 bg-yellow-400/10 border border-yellow-400/20">
                                            <Clock className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-semibold text-yellow-200">Pre-order</p>
                                                <p className="text-xs text-yellow-300/70">Releases {formatReleaseDate(releaseDate)}, pay now, download on release day</p>
                                            </div>
                                        </div>
                                    )}

                                    {isPWYW ? (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between rounded-xl p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                                <div className="flex items-center space-x-2">
                                                    <DollarSign className="w-4 h-4 text-white/30" />
                                                    <p className="text-sm text-white/60">Pay what you want</p>
                                                </div>
                                                {minimumPrice > 0 && <p className="text-xs text-white/30">min ${minimumPrice.toFixed(2)}</p>}
                                            </div>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                                                <input type="number" min={minimumPrice > 0 ? minimumPrice : 0} step="0.01" value={fanPrice}
                                                    onChange={e => { setFanPrice(e.target.value); setFanPriceError(''); }}
                                                    placeholder="Enter amount"
                                                    className="w-full pl-7 pr-4 py-3 bg-white/[0.06] rounded-xl text-white text-lg font-semibold outline-none focus:bg-white/[0.1] transition placeholder:text-white/20 text-center" />
                                            </div>
                                            {fanPriceError && <p className="text-xs text-red-400 text-center">{fanPriceError}</p>}
                                            {minimumPrice === 0 && <p className="text-xs text-white/30 text-center">Enter $0 to download free</p>}
                                            <div className="flex space-x-2">
                                                {[1, 2, 5, 10].filter(v => v >= minimumPrice).map(v => (
                                                    <button key={v} type="button" onClick={() => { setFanPrice(v.toFixed(2)); setFanPriceError(''); }}
                                                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition ${parseFloat(fanPrice) === v ? 'bg-white text-black' : 'bg-white/[0.06] text-white/50 hover:bg-white/[0.1]'}`}>
                                                        ${v}
                                                    </button>
                                                ))}
                                            </div>
                                            {parseFloat(fanPrice) > 0 ? (
                                                <>
                                                    {purchaseError && <p className="text-xs text-red-400 text-center">{purchaseError}</p>}
                                                    {/* Rand first when this seller is paid in rand, PayPal first when
                                                        they are not. PayOptions decides, and the PayPal container is
                                                        handed to it untouched: never hidden, never remounted.

                                                        The buyer's chosen amount goes with the rand path now, the way it
                                                        already did on the artist profile page. Without it a
                                                        pay-what-you-want offer was floored at the artist's minimum the
                                                        moment the buyer paid in rand. */}
                                                    <PayOptions trackId={track.id} amount={parseFloat(fanPrice) || 0}>
                                                        {!paypalReady && !purchaseError && <div className="flex justify-center py-2"><Loader className="w-5 h-5 animate-spin text-white/30" /></div>}
                                                        <div id="paypal-tas-container" style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '4px' }} />
                                                    </PayOptions>
                                                </>
                                            ) : (
                                                <button onClick={async () => {
                                                    if (!validateFanPrice()) return;
                                                    try {
                                                        const { data: { session } } = await supabase.auth.getSession();
                                                        if (!(isPreorder && isNotYetReleased)) {
                                                            await downloadTrack(track.id, track.title, session?.access_token);
                                                        } else {
                                                            setAlreadyPreordered(true);
                                                        }
                                                        onClose();
                                                    } catch {}
                                                }} className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition flex items-center justify-center space-x-2">
                                                    <Download className="w-4 h-4" />
                                                    <span>{isPreorder && isNotYetReleased ? 'Confirm Pre-order (Free)' : 'Download Free'}</span>
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between rounded-xl p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                                <div className="flex items-center space-x-2">
                                                    <Lock className="w-4 h-4 text-white/30" />
                                                    <p className="text-sm text-white/60">
                                                        {isPreorder && isNotYetReleased ? 'Pre-order to reserve your copy' : 'Purchase to download'}
                                                    </p>
                                                </div>
                                                <p className="text-lg font-bold text-white">
                                                    {priceUnknown ? ', ' : `$${effectivePrice.toFixed(2)}`}
                                                </p>
                                            </div>
                                            <PriceBreakdown quote={quote} sellerName={artist?.artist_name} />
                                            <p className="text-xs text-white/30 text-center">
                                                {isPreorder && isNotYetReleased
                                                    ? `High-quality MP3 delivered automatically on ${formatReleaseDate(releaseDate)}`
                                                    : 'High-quality MP3 delivered instantly after payment'}
                                            </p>
                                            {purchaseError && <p className="text-xs text-red-400 text-center">{purchaseError}</p>}
                                            {/* While the price is genuinely unknown, neither button is
                                                offered. Showing Download Free here is what handed out a
                                                button that 403s on a track that is for sale. */}
                                            {priceUnknown ? (
                                                quoteLoading ? (
                                                    <div className="flex justify-center py-3">
                                                        <Loader className="w-5 h-5 animate-spin text-white/30" />
                                                    </div>
                                                ) : (
                                                    <button onClick={() => refreshQuote()}
                                                        className="w-full py-3 bg-white/[0.06] text-white/60 text-sm font-semibold rounded-xl">
                                                        Could not check the price. Tap to try again.
                                                    </button>
                                                )
                                            ) : effectivePrice <= 0 ? (
                                                <button onClick={async () => {
                                                    try {
                                                        const { data: { session } } = await supabase.auth.getSession();
                                                        if (!(isPreorder && isNotYetReleased)) {
                                                            await downloadTrack(track.id, track.title, session?.access_token);
                                                        } else {
                                                            setAlreadyPreordered(true);
                                                        }
                                                        onClose();
                                                    } catch {}
                                                }} className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition flex items-center justify-center space-x-2">
                                                    <Download className="w-4 h-4" />
                                                    <span>{isPreorder && isNotYetReleased ? 'Confirm Pre-order (Free)' : 'Download Free'}</span>
                                                </button>
                                            ) : (
                                                <>
                                                    {/* Fixed price. Rand first when this seller is paid in rand. */}
                                                    <PayOptions trackId={track.id}>
                                                        {!paypalReady && !purchaseError && <div className="flex justify-center py-2"><Loader className="w-5 h-5 animate-spin text-white/30" /></div>}
                                                        <div id="paypal-tas-container" style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '4px' }} />
                                                    </PayOptions>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Main view */}
                    {view === 'main' && (
                        <>
                            <button onClick={handleLike} className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <Heart className="w-5 h-5" fill={liked ? '#ef4444' : 'none'} color={liked ? '#ef4444' : 'rgba(255,255,255,0.4)'} />
                                <span className="text-sm text-white/70">{liked ? 'Unlike' : 'Like'}</span>
                            </button>
                            {/* Comments. Same thread as the track page, the
                                album page and For You: one track_comments
                                thread per track_id, opened from everywhere. */}
                            <button onClick={() => setShowComments(true)} className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <MessageCircle className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">Comments</span>
                                {commentCount > 0 && (
                                    <span className="text-xs text-white/30 ml-auto">{commentCount}</span>
                                )}
                            </button>
                            <button onClick={handleQueue} className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <Play className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">Add to Queue</span>
                            </button>
                            <button onClick={loadPlaylists} className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <ListMusic className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">Add to Playlist</span>
                            </button>
                            <button onClick={handleShare} className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <Share2 className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">Share</span>
                            </button>

                            {/* Save for offline.
                                Above Download and not merged with it, because they are
                                different things and the wording has to make that obvious:
                                Download hands your phone an .mp3 outside the app, Offline
                                keeps the track inside Feelz Machine so it plays with no
                                signal. It also shows for tracks that are NOT downloadable -
                                an artist withholding the file is not the same as
                                withholding offline listening. */}
                            <OfflineSaveButton track={track} variant="row" onNavigate={onClose} />
                            {track.is_downloadable && track.id && (
                                <>
                                    {isPreorder && isNotYetReleased && alreadyPreordered ? (
                                        <div className="w-full flex items-center justify-between px-5 py-3.5">
                                            <div className="flex items-center space-x-4">
                                                <Clock className="w-5 h-5 text-yellow-400/60" />
                                                <span className="text-sm text-yellow-300/70">{downloadButtonLabel()}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={handleDownload} disabled={downloading}
                                            className="w-full flex items-center justify-between px-5 py-3.5 active:bg-white/[0.04] transition">
                                            <div className="flex items-center space-x-4">
                                                {downloading
                                                    ? <Loader className="w-5 h-5 animate-spin text-white/40" />
                                                    : isPreorder && isNotYetReleased
                                                        ? <Clock className="w-5 h-5 text-yellow-400/80" />
                                                        : isPWYW
                                                            ? <DollarSign className="w-5 h-5 text-white/40" />
                                                            : basePrice > 0
                                                                ? <ShoppingCart className="w-5 h-5 text-white/40" />
                                                                : <Download className="w-5 h-5 text-white/40" />}
                                                <span className={`text-sm ${isPreorder && isNotYetReleased ? 'text-yellow-300/80' : 'text-white/70'}`}>
                                                    {downloadButtonLabel()}
                                                </span>
                                            </div>
                                            {isPreorder && isNotYetReleased && !downloading && (
                                                <span className="text-xs text-yellow-400/40 ml-2">pre-order</span>
                                            )}
                                            {isPWYW && !isPreorder && !downloading && (
                                                <span className="text-xs text-white/20 ml-2">you choose</span>
                                            )}
                                        </button>
                                    )}
                                    {downloadError && <p className="text-xs text-red-400 px-5 pb-2">{downloadError}</p>}
                                </>
                            )}
                            {/* There was no way to reach a track's own page from
                                anywhere in the app: not the player, not the
                                artist profile, not this sheet. The page exists
                                at /track/:slug and /beat/:slug, has its own
                                crawler meta and is what Share copies, so it was
                                reachable by link but not by navigation. */}
                            {track?.slug && (
                                <button onClick={() => { navigate(`${track.is_beat ? '/beat' : '/track'}/${track.slug}`); onClose(); }}
                                    className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                    <Info className="w-5 h-5 text-white/40" />
                                    <span className="text-sm text-white/70">Go to track page</span>
                                </button>
                            )}
                            <button onClick={() => { const slug = artist?.slug || track?.artist_slug; if (slug) { navigate(`/artist/${slug}`); onClose(); } }}
                                className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <Music className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">View Artist</span>
                            </button>
                            <button onClick={async () => {
                                const slug = artist?.slug || track?.artist_slug;
                                const artistId = artist?.id || track?.artist_id;
                                if (!artistId) return;
                                const { data } = await supabase.from('tracks')
                                    .select('id, title, file_url, cover_artwork_url, duration, artist_id')
                                    .eq('artist_id', artistId).eq('is_published', true)
                                    .order('engagement_score', { ascending: false }).limit(20);
                                if (data?.length) {
                                    const queue = data.map(t => ({ ...t, artist_name: artist?.artist_name || track?.artist_name, artist_slug: slug }));
                                    playTrack(queue[0], queue);
                                }
                                onClose();
                            }}
                                className="w-full flex items-center space-x-4 px-5 py-3.5 active:bg-white/[0.04] transition">
                                <Play className="w-5 h-5 text-white/40" />
                                <span className="text-sm text-white/70">Play Artist Radio</span>
                            </button>
                        </>
                    )}
                </div>

                <div className="h-6" />
            </div>

            <style>{`
                @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                .animate-slide-up { animation: slideUp 0.25s ease-out; }
            `}</style>

        </div>

        {/* ShareCard outside sheet, avoids overflow-hidden clipping, matches FullPlayer pattern */}
        {showShareCard && (
            <ShareCard
                track={track}
                // Slug first: the song's name belongs in the link. The old
                // `slug || id` fallback shipped raw UUIDs whenever the screen
                // that opened this had not selected the slug column.
                shareUrl={buildShareUrl({
                    kind: track?.is_beat ? 'beat' : 'track',
                    slug: track?.slug,
                    shortCode: track?.short_code,
                })}
                onClose={() => setShowShareCard(false)}
            />
        )}

        {/* Comments, outside the sheet for the same reason ShareCard is: the
            sheet clips its overflow, and the composer is position:fixed. */}
        {showComments && (
            <TrackCommentsOverlay
                track={{ ...track, artist_name: track?.artist_name || artist?.artist_name }}
                user={user}
                routePrefix={track?.is_beat ? 'beat' : 'track'}
                onClose={() => { setShowComments(false); loadCommentCount(); }}
            />
        )}
        </>
    );
}